import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './AgeGroupEditPopup.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { getErrorMessage } from 'utils/api';
import {
  createClubAgeGroup,
  getClubAgeGroupUsage,
  updateClubAgeGroupById,
} from 'api/club/ageGroupApi';
import { toast } from 'react-toastify';
import { BOOLEAN_OPTIONS } from 'constants/options';
import useChangeDetection from 'hooks/useChangeDetection';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import {
  GROUPING_BASIS,
  groupingBasisOf,
  groupWords,
  formatGroupRange,
} from 'utils/groupLabel';

const AgeGroupEditPopup = ({
  isOpen,
  onClose,
  clubID,
  group,
  mode = 'edit', // 'edit', 'add'
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // A club that groups by year level reads Year Group here and enters the
  // range in years ("Y5-Y6"). The range is stored in the same "U" form as an
  // age club's ("U10-U11"): the server converts what is typed and checks it,
  // and the stored range is shown in years when a group is opened. See
  // utils/groupLabel.js.
  const { selectedClub } = useSelector((state) => state.user || {});
  const groupingBasis = groupingBasisOf(selectedClub);
  const byYearLevel = groupingBasis === GROUPING_BASIS.YEAR_LEVEL;
  // A club on the Age model types and sees the range in ages, with no "U"
  // ("10-11"); the server stores it in the same "U" form and refuses a "U".
  const byAge = groupingBasis === GROUPING_BASIS.AGE;
  const words = groupWords(groupingBasis);

  const [formData, setFormData] = useState({
    ageGroupName: '',
    ageRange: '',
    genderSplit: false,
    isVisible: false,
    sortOrder: '',
  });

  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  // Meet-program usage for the group being edited — drives the awareness
  // banner and the definition-change warning (age-group-lifecycle-design.md
  // §7.4). Null while loading or for add mode.
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    setUsage(null);
    if (!isOpen || mode !== 'edit' || !group?.ageGroupID || !clubID) {
      return;
    }
    let cancelled = false;
    getClubAgeGroupUsage(clubID, group.ageGroupID)
      .then((response) => {
        if (!cancelled) setUsage(response.data);
      })
      .catch((err) => {
        // Non-blocking: the popup still works without usage data.
        console.log('[AgeGroupEditPopup] Fetch usage error: ', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, group, clubID]);

  const currentData = {
    formData,
  };

  const { showPrompt, confirmLeave, cancelLeave, hasChanges, setShowPrompt } =
    useChangeDetection(originalData, currentData, isOpen);

  // Load data when editing
  useEffect(() => {
    if (mode === 'edit' && group) {
      const initialFormData = {
        ageGroupName: group.ageGroupName || '',
        ageRange: formatGroupRange(groupingBasis, group.ageRange || ''),
        genderSplit: group.genderSplit || false,
        isVisible: group.isVisible || false,
        sortOrder: group.sortOrder || '',
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    } else {
      const initialFormData = {
        ageGroupName: '',
        ageRange: '',
        genderSplit: false,
        isVisible: false,
        sortOrder: '',
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    }
    setErrors({});
  }, [mode, group, groupingBasis]);

  const handleInputChange = (name, value) => {
    if (name === 'sortOrder') {
      const numValue = parseInt(value);
      if (
        value &&
        value.toString().trim() &&
        !isNaN(numValue) &&
        numValue < 0
      ) {
        setErrors((prev) => ({
          ...prev,
          sortOrder: 'The sort order cannot be less than 0',
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ageGroupName.trim()) {
      newErrors.ageGroupName = `The ${words.lower.group} name is required`;
    }

    if (!formData.ageRange.trim()) {
      newErrors.ageRange = `The ${words.lower.value} range is required`;
    }
    if (!formData.sortOrder || formData.sortOrder.toString().trim() === '') {
      newErrors.sortOrder = 'The sort order is required';
    } else if (isNaN(parseInt(formData.sortOrder))) {
      newErrors.sortOrder = 'The sort order must be a valid number';
    } else if (parseInt(formData.sortOrder) < 0) {
      newErrors.sortOrder = 'The sort order cannot be less than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    if (mode === 'edit' && !hasChanges()) {
      onClose(false);
      return;
    }
    const editedGroup = {
      ageGroupName: formData.ageGroupName.trim(),
      ageRange: formData.ageRange.trim(),
      genderSplit: formData.genderSplit,
      isVisible: formData.isVisible,
      sortOrder: parseInt(formData.sortOrder),
    };
    setIsLoading(true);
    try {
      if (mode === 'edit') {
        await updateClubAgeGroupById(clubID, group.ageGroupID, editedGroup);
        toast.success(`${words.sentence.group} updated successfully`);
      } else {
        await createClubAgeGroup(clubID, editedGroup);
        toast.success(`${words.sentence.group} added successfully`);
      }
      setIsLoading(false);
      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (refresh) => {
    if (hasChanges()) {
      setShowPrompt(true);
      return;
    }
    onClose(refresh);
  };

  const handleConfirmLeave = () => {
    confirmLeave();
    onClose(false);
  };

  if (!isOpen) return null;

  const upcomingUsageCount = usage?.affectedMeets?.length || 0;

  // Definition (age range / gender split) edits get the stronger warning —
  // name and sort order changes are always safe (design §4 Tier 1 vs Tier 2).
  const definitionChanged =
    mode === 'edit' &&
    originalData?.formData &&
    (formData.ageRange.trim() !== (originalData.formData.ageRange || '').trim() ||
      formData.genderSplit !== originalData.formData.genderSplit);

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {mode === 'add' ? `Add ${words.group}` : `Edit ${words.group}`}
            </div>
            <div className={styles.form}>
              {upcomingUsageCount > 0 && (
                <div className={styles.usageBanner}>
                  Used in the {upcomingUsageCount === 1 ? 'program' : 'programs'}{' '}
                  of {upcomingUsageCount} upcoming{' '}
                  {upcomingUsageCount === 1 ? 'meet' : 'meets'}.
                </div>
              )}
              {upcomingUsageCount > 0 && definitionChanged && (
                <div className={styles.usageWarning}>
                  Meets already scheduled keep the current definition (&quot;
                  {originalData?.formData?.ageRange}&quot;). The new definition
                  applies when a program is created or re-saved.
                </div>
              )}
              <div className={styles.inputGroup}>
                <label className={styles.label}>{words.group} Name</label>
                <input
                  type="text"
                  value={formData.ageGroupName}
                  onChange={(e) =>
                    handleInputChange('ageGroupName', e.target.value)
                  }
                  className={`${styles.input} ${errors.ageGroupName ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder={`Enter ${words.lower.group} name`}
                />
                {errors.ageGroupName && (
                  <span className={styles.errorText}>
                    {errors.ageGroupName}
                  </span>
                )}
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>{words.value} Range</label>
                <input
                  type="text"
                  value={formData.ageRange}
                  onChange={(e) =>
                    handleInputChange('ageRange', e.target.value)
                  }
                  className={`${styles.input} ${errors.ageRange ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder={
                    byYearLevel
                      ? 'e.g., Y5 or Y5-Y6'
                      : byAge
                      ? 'e.g., 10 or 10-11'
                      : 'e.g., U6 or U6-U17'
                  }
                />
                {errors.ageRange && (
                  <span className={styles.errorText}>{errors.ageRange}</span>
                )}
                {byYearLevel ? (
                  <div className={styles.helpText}>
                    Enter year range (years 0 to 12) using formats like:
                    <ul className={styles.helpList}>
                      <li>
                        Single year: <strong>Y5</strong>
                      </li>
                      <li>
                        Range: <strong>Y5-Y6</strong>
                      </li>
                    </ul>
                  </div>
                ) : byAge ? (
                  <div className={styles.helpText}>
                    Enter the ages in the group, with no U, using formats
                    like:
                    <ul className={styles.helpList}>
                      <li>
                        Single age: <strong>10</strong>
                      </li>
                      <li>
                        Range: <strong>10-11</strong>
                      </li>
                      <li>
                        Several: <strong>8-10, 12</strong>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <div className={styles.helpText}>
                    Enter age range using formats like:
                    <ul className={styles.helpList}>
                      <li>
                        Single age: <strong>U6</strong>
                      </li>
                      <li>
                        Range: <strong>U6-U17</strong> or <strong>U6-17</strong>
                      </li>
                      <li>
                        Adult range: <strong>21-34</strong>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className={styles.twoColumnRow}>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Gender split"
                    name="genderSplit"
                    value={formData.genderSplit}
                    onChange={handleInputChange}
                    options={BOOLEAN_OPTIONS}
                    error={errors.genderSplit}
                    disabled={isLoading}
                    placeholder=""
                  />
                  {errors.genderSplit && (
                    <span className={styles.errorText}>
                      {errors.genderSplit}
                    </span>
                  )}
                  <div className={styles.helpText}>
                    Select Yes to display this group as two groups Male and
                    Female in event program groups list.
                  </div>
                </div>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Visible"
                    name="isVisible"
                    value={formData.isVisible}
                    onChange={handleInputChange}
                    options={BOOLEAN_OPTIONS}
                    error={errors.isVisible}
                    disabled={isLoading}
                    placeholder=""
                  />
                  {errors.isVisible && (
                    <span className={styles.errorText}>{errors.isVisible}</span>
                  )}
                  <div className={styles.helpText}>
                    Select Yes to Show in {words.lower.group} lists, select No
                    to hide.
                  </div>
                </div>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) =>
                    handleInputChange('sortOrder', e.target.value)
                  }
                  className={`${styles.input} ${errors.sortOrder ? styles.inputError : ''}`}
                  disabled={isLoading}
                  placeholder="Enter Sort Order"
                />
                {errors.sortOrder && (
                  <span className={styles.errorText}>{errors.sortOrder}</span>
                )}
              </div>
              <div className={styles.buttons}>
                <CustomNewButton
                  onClick={handleSave}
                  label="Save"
                  large={true}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="goBack" onClick={() => handleClose(false)}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={() => handleClose(false)}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      {/* Change Detection Confirmation Dialog */}
      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmLeave}
        confirmButtonText="Discard Changes"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
        cancelButtonText="Continue Editing"
      />
    </>
  );
};

AgeGroupEditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubID: PropTypes.string.isRequired,
  mode: PropTypes.string,
  group: PropTypes.object,
};

export default AgeGroupEditPopup;
